import { Html } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { ArrowUp } from 'lucide-react'
import { type CSSProperties, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import type { GameRuntime } from '../game/runtime'
import { buildProximityContacts } from '../ui/proximity-model'
import {
  getOverlayAnchor,
  getViewportCenter,
  placeOffscreenPoint,
  type ScreenPlacement,
  shouldShowOffscreenIndicators,
  spreadEdgeMarkers,
} from './offscreen-indicator-model'
import './offscreen-racer-indicators.css'

function calculateFullscreenCenter(
  _object: THREE.Object3D,
  _camera: THREE.Camera,
  size: { width: number; height: number },
): [number, number] {
  return getViewportCenter(size)
}

export interface OffscreenRacerIndicator extends ScreenPlacement {
  vehicleId: string
  name: string
  distance: number
  closingSpeed: number
}

export function OffscreenIndicatorLayer({ indicators }: { indicators: readonly OffscreenRacerIndicator[] }) {
  return (
    <div className="offscreen-racers" aria-hidden="true">
      {indicators.map((indicator) => (
        <div
          key={indicator.vehicleId}
          className="offscreen-racer"
          data-testid={`offscreen-racer-${indicator.vehicleId}`}
          data-edge={indicator.edge}
          data-danger={indicator.closingSpeed >= 10 ? 'true' : undefined}
          style={
            {
              '--indicator-x': `${indicator.xPercent}%`,
              '--indicator-y': `${indicator.yPercent}%`,
            } as CSSProperties
          }
        >
          <ArrowUp className="offscreen-racer__arrow" style={{ transform: `rotate(${indicator.arrowDegrees}deg)` }} />
          <div className="offscreen-racer__label">
            <strong>{indicator.name}</strong>
            <span>
              {Math.round(indicator.distance)} M {' · '}
              {indicator.closingSpeed >= 0 ? 'CLOSING' : 'OPENING'} {Math.round(Math.abs(indicator.closingSpeed) * 3.6)}{' '}
              KM/H
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

export function OffscreenRacerIndicators({ runtime }: { runtime: GameRuntime }) {
  const camera = useThree((state) => state.camera)
  const [indicators, setIndicators] = useState<OffscreenRacerIndicator[]>([])
  const anchorRef = useRef<THREE.Group>(null)
  const accumulator = useRef(0)
  const world = useMemo(() => new THREE.Vector3(), [])
  const cameraSpace = useMemo(() => new THREE.Vector3(), [])
  const projected = useMemo(() => new THREE.Vector3(), [])
  const cameraDirection = useMemo(() => new THREE.Vector3(), [])

  useFrame((_, delta) => {
    camera.getWorldDirection(cameraDirection)
    const anchor = getOverlayAnchor(camera.position, cameraDirection)
    anchorRef.current?.position.set(anchor.x, anchor.y, anchor.z)
    accumulator.current += delta
    if (accumulator.current < 0.08) return
    accumulator.current %= 0.08
    const state = runtime.getState()
    if (!shouldShowOffscreenIndicators(runtime.isPaused(), state.race.phase)) {
      if (indicators.length > 0) setIndicators([])
      return
    }
    const contacts = new Map(buildProximityContacts(state, 7).map((contact) => [contact.vehicleId, contact]))
    const next = state.vehicles.flatMap((vehicle): OffscreenRacerIndicator[] => {
      if (vehicle.id === state.race.playerId) return []
      const contact = contacts.get(vehicle.id)
      if (!contact) return []
      world.set(vehicle.position.x, 1.3, vehicle.position.y)
      cameraSpace.copy(world).applyMatrix4(camera.matrixWorldInverse)
      projected.copy(world).project(camera)
      const placement = placeOffscreenPoint({
        x: projected.x,
        y: projected.y,
        behind: cameraSpace.z > 0,
      })
      if (!placement) return []
      return [
        {
          ...placement,
          vehicleId: vehicle.id,
          name: contact.name,
          distance: contact.distance,
          closingSpeed: contact.closingSpeed,
        },
      ]
    })
    setIndicators(spreadEdgeMarkers(next.sort((a, b) => a.distance - b.distance)))
  })

  if (indicators.length === 0) return null
  return (
    <group ref={anchorRef}>
      <Html fullscreen calculatePosition={calculateFullscreenCenter} zIndexRange={[20, 10]}>
        <OffscreenIndicatorLayer indicators={indicators} />
      </Html>
    </group>
  )
}
